/**
 * 48コマ編集グリッド (spec AC-DFD-001 列構成に準拠)。
 *
 * 列構成 (spec AC-DFD-001、#3301/demand-fix-48-koma で決定):
 *   時刻 (HH:mm〜、AC-09) / 需要想定 (編集可、ExcelGrid操作対応、AC-11。
 *   最新の保存済みの値を直接表示し上書き編集する。ヘッダ下に取込元サブラベル、
 *   demand-48koma-columns AC-01/AC-02) / 差 (保存済みとの差分、3断面共通、
 *   spec AC-DFD-001、models/forecastDiff.ts の computeSaveDiff) / 当日実績値
 *   (当日断面のみ) / 実績との差分 (当日断面のみ、AC-10) / 過去実績 (最大5日分、
 *   AC-01/AC-02)。需要想定・保存済みの値を分けた専用列は独立に設けない
 *   (未確定状態では業務操作の確定を経た計画値が存在しないため、需要想定列
 *   自体が最新の保存済み値を表示・編集する、#3301)。
 *
 * spec 準拠改修 (demand-forecast-daily-v4) で以下の過剰実装列 / UI を削除した
 * (AC-01): 保存済み値の独立列・その変更案との差列 (旧3列構成。差列のみ後に
 * #3301 で復活)・MWh/kWh 単位切替・過去実績「採用」ボタン。spec 準拠調整
 * (demand-forecast-daily-spec-align, AC-08) で プロバイダ予測列も削除した
 * (Toolbar のプロバイダ切替チップ廃止に伴う連動削除)。確定履歴の比較列
 * (confirmComparisons) は spec 根拠の無い常時列だったため demand-48koma-columns
 * で全廃した (frontend 表示・BFF/internalapi proto field・backend 供給ロジックを
 * 削除、AC-06)。取込元サブラベルは旧実装 (611905b19 の親) から design 準拠で
 * 復活させた (AC-01/AC-02)。
 *
 * ExcelGrid 操作 (AC-11、story demand-008):
 * - 単一クリック (mousedown→同一セルでmouseup) は従来通り即座に編集モードに入る
 * - ドラッグ (mousedown→別セルへmousemove→mouseup) は編集に入らず範囲選択のみ行う
 * - 選択中セル (非編集) は矢印キー (↑/↓) でコマ移動
 * - F2 / ダブルクリックは既存値を保持したまま caret を末尾に置いて編集を開始する
 *   (単一クリックは値全体を選択して編集に入り、打鍵で置換する)
 * - Ctrl+C で選択範囲をコピー、Ctrl+V で選択開始位置から貼付 (~/models/gridSelection の
 *   純関数を利用。GCロック済みコマは貼付をスキップする)。貼付は `onPaste` 経由で
 *   呼び出し元へ通知し、単一セル編集 (`onChange`) とは別経路にする (spec
 *   AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ上書き」= 比例配分の対象外
 *   であることを型で保証する、demand-fix-edit-operation)。
 *
 * 旧シフト複数選択 (旧 AC-14、story demand-005) は demand-fix-edit-operation で撤去
 * 済み (P0 spec に選択集合を要求する AC が無いため)。
 *
 * 列ホバー強調 (AC-15): 時刻列にホバーした時、対応するチャート上のポイントを
 * 強調できるよう `onHoverKoma` で呼び出し元に通知する。
 *
 * GCロック行 (design L209/241 準拠): 確定済 (koma <= gcKoma) のコマは
 * warning 系低背景 + font-weight 700 で表示する。
 *
 * 現在コマ (currentKoma) のハイライトは **spec が要求する表示**。
 * AC-DFD-002 [P0] が「GC済みコマをロック表示し、現在コマをハイライト表示する」と
 * 1 文で両方を SHALL しており、GC ロックと同格の要求事項にあたる。
 *
 * **「design に無い本アプリ独自の付加情報」ではない** (2026-09-01 訂正)。design 側に
 * 無かったのはプロト側の実装漏れで、issue #6872 で「プロトが spec に反している 5 件」の
 * 1 つとして扱い、プロト側へ追加した。独自機能と書いてあると、次に 8080 準拠の突合を
 * する人が「spec に無いから消す」と判断しかねない (実際にその一歩手前まで行った)。
 *
 * 見せ方 (プロト = 細い青枠 / 実装 = 行全体の薄い青塗り) は AC-DFD-002 が規定して
 * いないためデザイナー判断。判断が出たらここを更新する。
 *
 * 空セルと未編集セルを視覚的に区別する (空セル = "—" 表示, 0 = 明示入力)。
 * read-only モード: isReadOnly=true のとき入力不可 (BG全体表示 / 完了ケース)。
 *
 * UI は ecloud-designsystem の Table / Input primitives で組む (= 生 HTML の
 * table / input は使わない)。色は LightModeSemanticColors トークンで参照する。
 */
import { Fragment, useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'

import {
  Box,
  Input,
  LightModeSemanticColors,
  Stack,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
  Tooltip,
} from '@enechain/ecloud-designsystem'
import { FaArrowDownRegular } from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import { HintTooltip } from '~/components/common/HintTooltip'
import { ToggleButton } from '~/components/common/ToggleButton'
import { iconSize } from '~/constants/iconSize'
import { KOMA_COUNT } from '~/constants/koma'
import { typography } from '~/constants/typography'
import { isEnterKey } from '~/libs/keyboard'
import {
  type CellPos,
  type CellRange,
  isInRange,
  makeRange,
  parseCellNumber,
  parseTsv,
  planPaste,
  rangeToTsv,
  stepCell,
} from '~/models/gridSelection'
import {
  KOMA_DIVIDER_BORDER,
  isKomaIndexDividerAfter,
} from '~/models/komaDivider'
import { komaNumberToTime } from '~/models/komaToTime'
import { formatKwh } from '~/models/kwhDisplay'
import { useKomaTheadSticky } from '~/pages/DemandForecastDaily/hooks/useKomaTheadSticky'
import { computeSaveDiff } from '~/pages/DemandForecastDaily/models/forecastDiff'
import type {
  ForecastReferenceColumnValueKind,
  ForecastSectionView,
} from '~/pages/DemandForecastDaily/models/forecastSectionView'
import {
  KOMA_COLUMN_WIDTH,
  MAX_REFERENCE_COLUMNS,
  hasTodayActuals,
} from '~/pages/DemandForecastDaily/models/komaTableLayout'
import {
  komaTableWrapOverflowStyle,
  komaTheadStickyStyle,
} from '~/pages/DemandForecastDaily/models/komaTheadSticky'
import { filterNumericImeInput } from '~/pages/DemandForecastDaily/models/numericInputFilter'
import type { PastActualColumnDisabledReasonKey } from '~/pages/DemandForecastDaily/models/pastActualRetailerSelection'
import {
  computeSlotDiff,
  formatSlotValue,
  isSavableSlotValue,
  roundSignedDelta,
  slotValueToEditText,
  updateKomaSlot,
} from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** 過去実績の追加列型 (確定履歴比較列は demand-48koma-columns AC-06 で全廃済み) */
type ExtraColumn = {
  key: string
  /**
   * 参照列「採用」(AC-DFD-047/048) の列識別キー。DOM の `key` (`pa-${idx}-
   * ${label}`、表示順 idx に依存) とは別に、`referenceColumns[].id` 由来の
   * 安定 key を持つ。列の並び替え・追加・削除があっても「採用中」判定が
   * 元の列からズレないようにするため。
   */
  adoptKey: string
  /**
   * AC-DFD-006a-6: 見出しのホバーでチャートの強調に使う系列 key。 ForecastChart の
   * dataKey と同じ形 (過去実績 = `past_{過去実績の中の順番}`、確定履歴 = `confirmRef_{id}`)。
   */
  seriesKey: string
  label: string
  width: string
  slots: KomaSlots
  valueKind: ForecastReferenceColumnValueKind
  /** gate review (#6048): 集計スコープの per-retailer 解決対象 0 件列。 */
  adoptUnavailable: boolean
  /**
   * 過去実績列限定の採用ボタン非活性理由キー (需要想定 過去実績「採用」の
   * 小売別取込 Design Note Decision 3/9)。`adoptUnavailable` (非表示) とは
   * 異なり、ボタンは描画したまま非活性 + tooltip で理由を示す。
   */
  adoptDisabledReasonKey?: PastActualColumnDisabledReasonKey
}

/**
 * `ExtraColumn.adoptDisabledReasonKey` → i18n key (需要想定 過去実績「採用」の
 * 小売別取込 Design Note Decision 3/9)。ページレベルの `adoptDisabledReason`
 * (KomaCard 側で解決済みの過去日/確定済み/小売未選択の文言) とは独立の、列単位の
 * 理由。
 */
const PAST_ACTUAL_ADOPT_DISABLED_REASON_I18N_KEYS: Record<
  PastActualColumnDisabledReasonKey,
  string
> = {
  notBrokenDown: 'page:demandForecastDaily.koma.adoptDisabledNotBrokenDown',
  noRetailerActual:
    'page:demandForecastDaily.koma.adoptDisabledNoRetailerActual',
}

type Props = {
  /**
   * 断面の描画専用値型 (D8、slice 2)。系列 (slots / baselineSlots /
   * todayActualSlots / referenceColumns=pastActualSlots)、cells
   * (manualEditKomas / dirtyKomas / gcKoma / currentKoma)、editing
   * (isReadOnly / isAggregateScope / adoptedReferenceColumnKey) はすべてここから読む (`buildForecastSectionView`、
   * index.tsx が useMemo で 1 回だけ呼ぶ)。sourceLabel / adoptDisabledReason
   * (i18n 文言) と stickyHeaderTop (実測) は presentation のため view に
   * 入れず呼び出し元 (KomaCard) が個別 prop で渡す。
   */
  view: ForecastSectionView
  /**
   * 需要想定値列ヘッダ下のサブラベル (design `.th-source` 相当、取込元。
   * demand-48koma-columns AC-01/AC-02)。非空なら描画、空文字/undefined なら
   * 非描画。readonly ビュー・BG 集計ビューでの非表示判断は呼び出し元
   * (KomaCard) が sourceLabel を渡さないことで表現する (= 本コンポーネントは
   * 非空チェックのみ)。
   */
  sourceLabel?: string
  /** 単一セル編集 (手入力) のコミット時に呼ばれる。貼付は `onPaste` 経由 (別 signature)。 */
  onChange?: (koma: number, value: number | undefined, slots: KomaSlots) => void
  /**
   * 貼付 (Ctrl+V) 確定時に呼ばれる (spec AC-DFD-006「貼付は補正モードを問わず
   * 貼付範囲のみ上書き」)。`onChange` (単一セル編集、比例配分の対象) とは別
   * signature に分けることで、貼付が比例配分を経由しない意図を可読化する
   * (demand-fix-edit-operation)。
   */
  onUndoAggregate?: () => void
  inputParser?: (raw: string) => number | undefined
  onPaste?: (startKoma: number, nextSlots: KomaSlots) => void
  /**
   * 列ヘッダー行の貼り付き位置 (px)。上に重なる sticky 帯 = 48コマ表カードの
   * 見出しバーの実測高。既定 0 (帯が無い / 測れていない)。
   * 詳細は models/komaTheadSticky の JSDoc。
   */
  stickyHeaderTop?: number
  /** AC-15: 列 (時間帯) ホバー時に呼び出し元へ通知する (対応するチャート強調に使う) */
  onHoverKoma?: (koma: number | undefined) => void
  /**
   * AC-DFD-006a-6: 参照列 (当日実績・過去実績・確定履歴) の列見出しにホバーした時、
   * チャートで強調する系列のグループ key (ForecastChart の `emphasizedSeriesKey`) を
   * 通知する。見出しから外れたら undefined。
   */
  onHoverSeriesKey?: (seriesKey: string | undefined) => void
  /**
   * AC-DFD-047/048: 参照列ヘッダー「採用」ボタン押下。押下中の同列を再度
   * 押下した場合も同じコールバックが呼ばれ、採用/解除 (AC-DFD-047b) の
   * 判定は呼び出し元 (`useForecastDraftEditor`) が `adoptedReferenceColumnKey`
   * との一致で行う。`valueKind` は列の種別 (過去実績/確定履歴) を明示する
   * (需要想定 過去実績「採用」の小売別取込 Design Note レビュー指摘: 呼び出し元
   * 〈index.tsx〉が帰属不明分通知の対象を「過去実績列の採用」だけに絞る時、
   * 列 key の日付フォーマットとの非衝突に暗黙で依存させない)。
   */
  onAdoptReferenceColumn?: (column: {
    key: string
    sourceLabel: string
    slots: KomaSlots
    valueKind: ForecastReferenceColumnValueKind
  }) => void
  /**
   * 採用ボタンの非活性理由 (isReadOnly=true のときのみ tooltip 表示)。
   */
  adoptDisabledReason?: string
}

/**
 * 行高: chakra の `TableRoot size="sm"` は root へ textStyle `sm`
 * (`lineHeight: 1.25rem` = 20px 固定) を当てるため、design (`#koma-table` は
 * line-height 未指定 = UA 既定 normal) より 1 行あたり数 px 高くなる。app 自身の
 * 役割定義 (`~/constants/typography` の tableHeader / tableCell = md/semibold/1.3、
 * md/1.3) を inline style で明示して chakra host の 20px を打ち消す
 * (fontSize / fontWeight の値は従来と同一。lineHeight だけが増える)。
 */
/**
 * 列ヘッダーの罫線は border ではなく inset box-shadow で描く。
 *
 * `border-collapse: collapse` の表では罫線を table 側が描くため、ヘッダー行を
 * sticky で貼り付けるとセルだけが動いて**下罫線が置き去りになる**。姉妹画面の
 * 共通部品 (`~/components/planSubmit/PlanTable`) が同じ理由で同じ手を採っている。
 * 見た目は 1px 同色で不変。
 *
 * 参照列だけが持つ左の区切り線も同じ理由で shadow 側へ移す (AC-DFD-047 / 048 で
 * 入れた区切り。border のままだと貼り付いた時に消える)。
 */
const HEADER_BOTTOM_SHADOW = `inset 0 -1px 0 ${LightModeSemanticColors.Border.lowEmphasis.value}`
const HEADER_LEFT_SHADOW = `inset 1px 0 0 ${LightModeSemanticColors.Border.lowEmphasis.value}`

const headerCellStyle: React.CSSProperties = {
  ...typography.tableHeader,
  padding: '6px 4px',
  boxShadow: HEADER_BOTTOM_SHADOW,
  color: LightModeSemanticColors.Text.highEmphasis.value,
  whiteSpace: 'nowrap',
  textTransform: 'none',
  letterSpacing: 'normal',
  background: LightModeSemanticColors.Surface.tableHeader.value,
}

const bodyCellStyle: React.CSSProperties = {
  ...typography.tableCell,
  padding: '4px',
  borderBottom: `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`,
  whiteSpace: 'nowrap',
}

/**
 * コマ行のセルの style。 3 時間 (6 コマ) ごとの区切りの行だけ下線を濃くする
 * (conventions.md §48 コマの表の区切り線。 線は各セルが持つのでセル側で変える)。
 */
const komaBodyCellStyle = (komaIndex: number): React.CSSProperties =>
  isKomaIndexDividerAfter(komaIndex)
    ? { ...bodyCellStyle, borderBottom: KOMA_DIVIDER_BORDER }
    : bodyCellStyle

/**
 * 参照列 (過去実績 / 確定履歴) の左区切り線。design forecast-today.html:416-417
 * `#koma-table th.dft-th-past, #koma-table td.dft-td-past,
 *  #koma-table th.dft-th-confirmed, #koma-table td.dft-td-confirmed
 *  { border-left: 1px solid var(--border-low) }` 準拠。design のセレクタは参照列の
 * 「各列」にマッチするので、本体列と参照列の境目だけでなく参照列どうしの境目にも
 * 1 本ずつ入る (= extraColumns 全列に付ける)。太さ・色は同じ表の borderBottom と
 * 同一 token に揃える (design 側コメント「太さ・色は既存の表セルに統一」)。
 */
const referenceCellBorderLeft = `1px solid ${LightModeSemanticColors.Border.lowEmphasis.value}`

/** 値未確定/未編集セルの「—」表示 (design の各 `koma-cell-copyable` 既定色 = mid-emphasis) */
const EmptyCell = (): React.ReactElement => (
  <Text as="span" color="Text.midEmphasis" fontSize="md">
    —
  </Text>
)

/**
 * design `.th-source` (forecast-today.html:348) 相当: 需要想定値列ヘッダ下の
 * 取込元サブラベル (demand-48koma-columns AC-01)。design は
 * `color: var(--text-mid-emphasis); font-weight: 400` を指定 (旧実装
 * 611905b19 の親は色のみ Brand.cyan だったが、現行 design の値に合わせる)。
 * fontWeight="normal" は親 TH の 600 継承を打ち消すために必須 (無指定だと
 * サブラベルが太字になり design / 旧実装のいずれとも不一致になる)。
 *
 * **`whiteSpace="normal"` も必須。** 親 `headerCellStyle` は列見出しを 1 行に保つため
 * `whiteSpace: nowrap` を持つが、サブラベルまで nowrap のままだと**長い文言が列幅を
 * 押し広げる**。取込元名 (「単純前日コピー」等) だけを想定していた頃は短く収まって
 * いたが、当日断面で計画が無い時の案内文がここに入るようになり、 `draftValue` 列
 * (78px) が実測 4 倍以上に広がっていた (2026-09-03 デザイナー指摘:
 * 「小売選択後にカラムが広がる」)。案内文は短縮したが、取込元名も可変なので
 * 折り返しの指定は引き続き必要になる。
 *
 * 列幅は `models/komaTableLayout.ts` の `KOMA_COLUMN_WIDTH` 定数の総和で
 * カード幅 (`naturalKomaTableCardWidth`) を決めているので、実測が定数を超えると
 * カード幅の計算ごと崩れる。折り返せば TH の `width` が効き、見出しが縦に伸びる
 * だけで列幅は定数どおりに保たれる。
 *
 * 和文は通常の改行機会で折れるが、ファイル名や ID など空白のない英数字列は
 * `overflowWrap="anywhere"` がないと折れない。短い取込元名は 78px に収まるため、
 * 従来どおり 1 行で表示される。
 */
const SourceSubLabel = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => (
  // eslint-disable-next-line ebalance/no-adhoc-typography -- design forecast-today.html:348 .th-source = xs/400 と一致。xs/normal の role は未定義のため textStyle 化見送り (旧実装 611905b19 の親と同じ判断)
  <Box
    as="span"
    display="block"
    color="Text.midEmphasis"
    fontSize="xs"
    fontWeight="normal"
    whiteSpace="normal"
    /* `normal` だけでは空白のない英数字列を折り返せない。取込元名は「簡易予測」
       のような日本語だけでなく、 ファイル名や ID がそのまま入る経路があり
       (CSV 取込・確定履歴)、
       空白を含まない英数字列だと 1 行のまま伸びて列幅を押し広げ、 隣の列を潰す
       (2026-09-03 レビュー指摘)。 `anywhere` で空白が無くても折り返せるようにする。
       `break-all` ではなく `anywhere` なのは、 空白があるならそちらを優先して
       単語の途中で切らないため。 */
    overflowWrap="anywhere"
  >
    {children}
  </Box>
)

/**
 * 「差」列 (spec AC-DFD-001「差（保存済みとの差分）」): 保存済み
 * baseline と編集中の値 (current) の差を符号付きで表示する
 * (models/forecastDiff.ts の computeSaveDiff、AC-03 Q6-A 4分岐)。3断面共通で常時
 * 表示する (AC-01)。色は design forecast-today.html:4497-4499 (生きている描画経路
 * `_dftRenderTable`) 準拠で ActualDiffCell と同じ対称配色にする (増加 =
 * positiveHigh、減少 = negativeHigh、0/空 = EmptyCell の midEmphasis)。
 * 旧コメントの「design L245-246 準拠で非対称配色」は撤回した — 現行 design の
 * L245-246 は無関係の CSS で、根拠だった `.koma-table .diff-pos/.diff-neg`
 * (同 257-258) も dead renderer (`_dftRenderKomaTable`) からしか参照されていない。
 *
 * AC-05 の色 assert 方針: `color` prop の token 文字列を `data-diff-color`
 * 属性でも machine-readable に公開する (raw computed style に依存させない)。
 */
const SaveDiffCell = ({
  baseline,
  current,
}: {
  baseline: number | undefined
  current: number | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  const { display, sign } = computeSaveDiff(baseline, current)
  if (display === '') return <EmptyCell />
  // sign === null は display === '' の早期 return で EmptyCell に落ちるため、
  // ここに来るのは '+' / '-' の 2 値のみ (design forecast-today.html:4499 の
  // 第 3 分岐 `0 → mid-emphasis` は EmptyCell が担う)。
  const colorToken = sign === '+' ? 'Text.positiveHigh' : 'Text.negativeHigh'
  return (
    <Text
      as="span"
      color={colorToken}
      fontSize="md"
      aria-label={t('page:demandForecastDaily.koma.saveDiffAriaLabel', {
        value: display,
      })}
      data-diff-color={colorToken}
      fontVariantNumeric="tabular-nums"
    >
      {display}
    </Text>
  )
}

/**
 * 「実績との差分」列 (spec AC-001、design doc AC-10): 実績 (actual) − 想定 (forecast)。
 * 送電端 kWh/30min (AC-DFC-011) の絶対値でなく符号付き差分をプラス/マイナス
 * 色分け表示する。パーセント表示は含まない。
 */
const ActualDiffCell = ({
  forecast,
  actual,
}: {
  forecast: number | undefined
  actual: number | undefined
}): React.ReactElement => {
  const { t } = useTranslation()
  const diff = computeSlotDiff(forecast, actual)
  if (diff === undefined) return <EmptyCell />
  // 符号・色は「表示する値」で判定する。実績は送電端値で小数を持つため、丸める前の
  // delta で判定すると 0.5 未満の差が「+0」「▲0」(値ゼロなのに増減色) になる。
  const roundedDelta = roundSignedDelta(diff.delta)
  const isPositive = roundedDelta > 0
  const isNegative = roundedDelta < 0
  const color = isPositive
    ? LightModeSemanticColors.Text.positiveHigh.value
    : isNegative
      ? LightModeSemanticColors.Text.negativeHigh.value
      : LightModeSemanticColors.Text.midEmphasis.value
  const sign = isPositive ? '+' : isNegative ? '▲' : ''
  const absDelta = Math.abs(roundedDelta)
  return (
    <Text
      as="span"
      // a11y: 色+符号で区別 (色のみ依存しない)
      color={color}
      fontSize="md"
      aria-label={t('page:demandForecastDaily.koma.actualDiffAriaLabel', {
        sign,
        value: formatKwh(absDelta),
      })}
      fontVariantNumeric="tabular-nums"
    >
      {sign}
      {formatKwh(absDelta)}
    </Text>
  )
}

export const KomaGrid = ({
  view,
  sourceLabel,
  onChange,
  onPaste,
  inputParser,
  onUndoAggregate,
  stickyHeaderTop = 0,
  onHoverKoma,
  onHoverSeriesKey,
  onAdoptReferenceColumn,
  adoptDisabledReason,
}: Props): React.ReactElement => {
  // D8 (slice 2): 系列・per-koma 状態・編集 gate は view から destructure する
  // (受け口をここに閉じ、以降の local 名は既存のまま使う)。
  const {
    series: {
      forecast: slots,
      todayActual: todayActualSlots,
      referenceColumns: pastActualSlots,
    },
    cells: { manualEditKomas, dirtyKomas, gcKoma, currentKoma },
    editing: {
      savedBaseline: baselineSlots,
      isReadOnly,
      adoptedReferenceColumnKey,
    },
  } = view
  const { t } = useTranslation()
  // GC ロック理由 (AC-DFD-002 系, 2026-09-02 デザイナー指摘): 48 コマ全行で同一文言
  // のため .map() の外で 1 度だけ翻訳し、ロック行ごとの再翻訳コストを避ける。
  const gcLockedTooltipText = t('page:demandForecastDaily.koma.gcLockedTooltip')
  const [editingKoma, setEditingKoma] = useState<number | null>(null)
  const [editValue, setEditValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const [pasteSkippedCount, setPasteSkippedCount] = useState(0)
  const [pasteReadOnlySkippedCount, setPasteReadOnlySkippedCount] = useState(0)
  const slotsRef = useRef(slots)
  slotsRef.current = slots
  const pasteRequestIdRef = useRef(0)

  // ── AC-11 ExcelGrid: 選択範囲 (ドラッグ範囲選択・矢印キー移動の対象) ────────
  const [selectionRange, setSelectionRange] = useState<CellRange | null>(null)
  const [selectionFocus, setSelectionFocus] = useState<CellPos>({
    row: 0,
    col: 1,
  })
  const selectionAnchorRef = useRef<CellPos | null>(null)
  const focusSyncGuardRef = useRef<CellPos | null>(null)
  const dragStartKomaRef = useRef<CellPos | null>(null)
  const didDragRef = useRef(false)
  const cellRefs = useRef<Record<string, HTMLElement | null>>({})
  const cellKey = (pos: CellPos): string => `${pos.row}:${pos.col}`

  // spec AC-01/AC-02「過去実績」は最大5日分 (demand-forecast-daily-spec-align で
  // 3→5 に拡大)。確定履歴の参照列 (s07-confirm-history-import-reference-view.md
  // Decision #3) も独立 5 件上限で、`buildForecastSectionView`
  // (`series.referenceColumns`、D7) が過去実績列と concat してから
  // `view.series.referenceColumns` としてこの component へ渡るため、本
  // コンポーネント側の defensive cap は合算上限の 10 件 (= 5+5) にする。各
  // ソース自身の 5 件上限は呼び出し元 (Toolbar の過去実績 5 件制御 /
  // useConfirmRecordImportFlow の MAX_CONFIRM_RECORD_REFS) が担う。
  const cappedPastActualSlots = pastActualSlots.slice(0, MAX_REFERENCE_COLUMNS)

  const showTodaySection = hasTodayActuals(todayActualSlots)

  // 列ヘッダー行の貼り付き (design/CLAUDE.md「thead の position: sticky は
  // overflow-x: auto と併用しない」)。判定と理由は models/komaTheadSticky の
  // JSDoc が SoT。貼り付き先はワークスペースの overflow:auto なので、
  // **48コマ表カード側に overflow を持つ器を足さないこと** (足すと sticky の
  // 効き先がそちらへ移り、列名がページと一緒に流れなくなる)。
  const { wrapRef, tableRef, isSticky } = useKomaTheadSticky({
    referenceColumnCount: cappedPastActualSlots.length,
    showTodaySection,
  })
  const theadStickyStyle = isSticky
    ? komaTheadStickyStyle(stickyHeaderTop)
    : undefined

  // --- 追加列 (pastActual + 確定履歴参照列) ---
  // key は index を含めて一意化する (確定履歴参照列は同日に複数件確定される
  // ケースがあり label だけでは衝突しうるため、pastActual との統一フォーマットとして
  // index を常に含める)。
  // 過去実績はチャートの `past_{idx}` (過去実績だけで数えた順番) に、確定履歴は
  // `confirmRef_{id}` に対応する。 参照列は過去実績 → 確定履歴の順に並ぶ (forecastSectionView)。
  let pastActualIndex = 0
  const extraColumns: ExtraColumn[] = cappedPastActualSlots.map(
    (pa, idx): ExtraColumn => {
      const seriesKey =
        pa.valueKind === 'actual'
          ? `past_${String(pastActualIndex++)}`
          : `confirmRef_${pa.id}`
      return {
        key: `pa-${idx}-${pa.label}`,
        adoptKey: pa.id,
        seriesKey,
        label: pa.label,
        width: `${KOMA_COLUMN_WIDTH.reference}px`,
        slots: pa.slots,
        valueKind: pa.valueKind,
        adoptUnavailable: pa.adoptUnavailable ?? false,
        adoptDisabledReasonKey: pa.adoptDisabledReasonKey,
      }
    },
  )
  const visibleColumnCount =
    3 + (showTodaySection ? 2 : 0) + extraColumns.length
  /** 矢印移動・貼付先算出に使うグリッド境界 (48 コマ × 表示列数) */
  const gridBounds = { rowCount: KOMA_COUNT, colCount: visibleColumnCount }

  /**
   * 参照列「採用」ボタン (AC-DFD-047/047a/047b, AC-DFD-048/048a): 採用元
   * サブラベル (Decision 5) — 過去実績列は「過去実績 {日付}」、確定履歴列は
   * 既存 `ref.label`（"確定 MM/dd"）をそのまま流用する。
   */
  const adoptedSourceLabel = (col: ExtraColumn): string =>
    col.valueKind === 'forecast'
      ? col.label
      : t('page:demandForecastDaily.koma.adoptedSourceLabelPastActual', {
          date: col.label,
        })

  const renderExtraColumnHeader = (col: ExtraColumn): React.ReactElement => {
    // AC-DFD-047 / 048 (issue #7436): 過去実績・確定履歴とも集計スコープ
    // (BG 全体×配下複数) でも「採用」ボタンを描画する (各小売の帰属値を
    // そのまま取り込む、AC-DFD-047d)。gate review (#6048):
    // col.adoptUnavailable (呼び出し元が per-retailer 解決対象 0 件と判定した
    // 確定履歴列) は非表示にする (押しても no-op になる「表示と実行の乖離」
    // 防止、principles.md 鉄則1)。
    const hideAdoptButton = col.adoptUnavailable
    const isAdopted =
      adoptedReferenceColumnKey !== undefined &&
      adoptedReferenceColumnKey === col.adoptKey
    // 需要想定 過去実績「採用」の小売別取込 Design Note Decision 3/9: page-level
    // の理由 (過去日/確定済み/小売未選択、KomaCard.tsx の
    // ADOPT_DISABLED_REASON_I18N_KEYS が明文化する優先順位「過去日 > 確定済み >
    // 小売未選択」) を列単位の理由 (分解不能/当該小売の実績なし) より優先する —
    // page-level の理由がある間は「押しても意味がない」の根本原因がそちらであり、
    // 列理由を先に出すと「マスタを直せば押せる」と誤読させる (レビュー指摘)。
    // 列理由は page-level 理由が無い (= 編集可能な断面で小売も選択済み) ときに
    // 初めて意味を持つ。
    const columnDisabledReason =
      col.adoptDisabledReasonKey !== undefined
        ? t(
            PAST_ACTUAL_ADOPT_DISABLED_REASON_I18N_KEYS[
              col.adoptDisabledReasonKey
            ],
          )
        : undefined
    const effectiveDisabledReason = adoptDisabledReason ?? columnDisabledReason
    const adoptButton = (
      <ToggleButton
        aria-label={
          isAdopted
            ? t('page:demandForecastDaily.koma.adoptedButtonAriaLabel', {
                label: col.label,
              })
            : t('page:demandForecastDaily.koma.adoptButtonAriaLabel', {
                label: col.label,
              })
        }
        disabled={effectiveDisabledReason !== undefined}
        onClick={() => {
          onAdoptReferenceColumn?.({
            key: col.adoptKey,
            sourceLabel: adoptedSourceLabel(col),
            slots: col.slots,
            valueKind: col.valueKind,
          })
        }}
        pressed={isAdopted}
        size="xs"
      >
        {isAdopted
          ? t('page:demandForecastDaily.koma.adoptedButton')
          : t('page:demandForecastDaily.koma.adoptButton')}
      </ToggleButton>
    )
    return (
      <TableColumnHeader
        key={col.key}
        onMouseEnter={() => {
          onHoverSeriesKey?.(col.seriesKey)
        }}
        onMouseLeave={() => {
          onHoverSeriesKey?.(undefined)
        }}
        style={{
          ...headerCellStyle,
          ...theadStickyStyle,
          width: col.width,
          textAlign: 'right',
          // 左の区切りも shadow 側へ (HEADER_LEFT_SHADOW の JSDoc 参照)。
          boxShadow: `${HEADER_BOTTOM_SHADOW}, ${HEADER_LEFT_SHADOW}`,
        }}
      >
        <Stack alignItems="flex-end" gap={1}>
          {col.label}
          {!hideAdoptButton &&
            (effectiveDisabledReason !== undefined ? (
              <Tooltip
                content={effectiveDisabledReason}
                placement="top"
                showArrow
              >
                <span>{adoptButton}</span>
              </Tooltip>
            ) : (
              adoptButton
            ))}
        </Stack>
      </TableColumnHeader>
    )
  }

  const renderExtraColumnCell = (
    col: ExtraColumn,
    komaIndex: number,
    fontWeight: string | undefined,
    gridCol: number,
    selected: boolean,
    /**
     * 現在コマ行の輪郭 (design `.koma-row-current td`)。行単位の判定は呼び出し元が
     * 持つので受け取る。無選択のときだけ出す (選択枠が優先)。
     */
    currentKomaOutline: string | undefined,
  ): React.ReactElement => {
    const cellValue = col.slots[komaIndex]
    return (
      <TableCell
        key={col.key}
        ref={(el: HTMLTableCellElement | null) => {
          cellRefs.current[cellKey({ row: komaIndex, col: gridCol })] = el
        }}
        textAlign="right"
        data-grid-col={gridCol}
        data-grid-row={komaIndex}
        data-selected={selected || undefined}
        style={{
          ...komaBodyCellStyle(komaIndex),
          borderLeft: referenceCellBorderLeft,
          boxShadow: selected
            ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
            : currentKomaOutline,
        }}
        tabIndex={
          selectionFocus.row === komaIndex && selectionFocus.col === gridCol
            ? 0
            : -1
        }
      >
        {cellValue === undefined ? (
          <EmptyCell />
        ) : (
          // eslint-disable-next-line ebalance/no-adhoc-typography -- fontWeight は呼び出し元 (lockedFontWeight) 経由の動的値のため静的 textStyle 化不能。chakra Table host の ambient lineHeight 制約もあり (design 併記: td=md/400、td:first-child=md/600、.gc-row td=md/700)
          <Text
            as="span"
            color="Text.highEmphasis"
            fontSize="md"
            fontWeight={fontWeight}
            fontVariantNumeric="tabular-nums"
          >
            {col.valueKind === 'actual'
              ? formatKwh(cellValue)
              : formatSlotValue(cellValue)}
          </Text>
        )}
      </TableCell>
    )
  }

  /** コマが GC済みでロックされているか (gcKoma > 0 かつ koma <= gcKoma) */
  const isKomaGcLocked = (koma: number): boolean => gcKoma > 0 && koma <= gcKoma
  const isKomaEditable = (koma: number): boolean =>
    !isReadOnly && !isKomaGcLocked(koma)

  // startEdit ごとに単調増加する編集セッション ID。非同期 (クリップボード読取)
  // の完了時に、貼付開始時と同じ編集セッションかを識別するために使う。
  const editSessionRef = useRef(0)

  // 同じ編集セッション内で「貼付開始後に入力操作があったか」を見分けるための
  // 編集リビジョン。下書き文字列の比較では、いったん別の値を入力してから貼付
  // 開始時と同じ文字列へ戻したケースを新しい入力と区別できないため、input の
  // onChange ごとに増やすカウンタで判定する。
  const editRevisionRef = useRef(0)

  // 編集セッション開始時点の editRevisionRef。今の値と異なれば、その編集
  // セッションで入力があった。
  const editRevisionAtEditStartRef = useRef(0)

  /**
   * コマの編集を開始する。`caret` は編集開始時の input の選択状態で、
   * `'select'` は値全体を選択 (打鍵で置換)、`'end'` は既存値を保持して caret を
   * 末尾に置く (conventions.md「Excelライクなグリッド編集」§選択と編集の状態を
   * 分ける: F2 / ダブルクリック)。`'end'` で既にそのコマを編集中なら、入力途中の
   * 値と編集セッションを保ったまま caret だけを末尾に置く。
   */
  const startEdit = (
    koma: number,
    caret: 'select' | 'end' = 'select',
  ): void => {
    if (!isKomaEditable(koma)) return
    if (caret === 'select' || editingKoma !== koma) {
      editSessionRef.current += 1
      editRevisionAtEditStartRef.current = editRevisionRef.current
      setEditingKoma(koma)
      // 編集対象コマを選択範囲にも同期する。Enter/Tab による編集セル移動
      // (handleInputKeyDown) では選択範囲が移動前のコマに取り残されるため、
      // 同期しないと編集中の Ctrl+C / Ctrl+V が別コマを起点にしてしまう。
      const pos = { row: koma - 1, col: 1 }
      selectionAnchorRef.current = pos
      setSelectionFocus(pos)
      setSelectionRange(makeRange(pos, pos))
      setEditValue(slotValueToEditText(slots[koma - 1]))
    }
    // 単クリック直後のダブルクリックでは、1 回目の押下で予約した select() より
    // 後に caret を置く必要があるため、同じく setTimeout で後ろに並べる。
    setTimeout(() => {
      const input = inputRef.current
      if (input === null) return
      input.focus()
      if (caret === 'end') {
        input.setSelectionRange(input.value.length, input.value.length)
      } else {
        input.select()
      }
    }, 0)
  }

  const handleCellDoubleClick = (pos: CellPos): void => {
    if (pos.col !== 1) return
    const koma = pos.row + 1
    // 編集できないコマはダブルクリックのネイティブ単語選択を残さない (48点データの
    // ロックセル P5 と同じ。範囲コピーは独自の Ctrl+C なのでネイティブ選択は不要)
    if (!isKomaEditable(koma)) {
      window.getSelection()?.removeAllRanges()
      return
    }
    // 編集開始後に入力があれば、ネイティブの単語選択を残す (入力途中の編集を
    // 妨げない。React mock の ExcelGrid が編集中 input 上の dblclick を無視するのと同じ)
    if (
      editingKoma === koma &&
      editRevisionRef.current !== editRevisionAtEditStartRef.current
    ) {
      return
    }
    startEdit(koma, 'end')
  }

  const commitEdit = (): void => {
    if (editingKoma === null) return
    const komaBeingEdited = editingKoma
    const trimmed = editValue.trim()
    let value: number | undefined
    if (trimmed === '') {
      value = undefined
    } else {
      const parsed =
        inputParser === undefined
          ? parseFloat(trimmed.replace(/,/g, ''))
          : inputParser(trimmed.replace(/,/g, ''))
      // 数値でない値と backend の長さ上限を超える値は保存できないため既存値を残す
      if (
        parsed === undefined ||
        isNaN(parsed) ||
        !isSavableSlotValue(parsed)
      ) {
        value = slots[komaBeingEdited - 1]
      } else {
        // 表示単位と保存単位はどちらも送電端 kWh/30min (AC-DFC-011) のため、
        // 入力値をそのまま保存値として扱う。
        value = parsed
      }
    }
    setPasteSkippedCount(0)
    setPasteReadOnlySkippedCount(0)
    // 値が変わっていない no-op 編集 (セルをクリックして変更せず blur 等) では
    // onChange を呼ばない。呼ぶと親 (handleKomaChange) が isDirty: true にして
    // manualEditKomas に追加するため、確定ボタン (allRetailersSaved = !isDirty)
    // が無効化され、変更していないのに不要な保存を強いられる。
    if (value !== slots[komaBeingEdited - 1]) {
      // 単一セル編集 (旧シフト複数選択の一括補正〈AC-14〉は demand-fix-edit-
      // operation で撤去済み)。編集対象コマ自体は GC ロック時は startEdit が
      // 編集開始を拒否しているため、ここでは常に単一コマ更新でよい。
      const nextSlots = updateKomaSlot(slots, komaBeingEdited, value)
      onChange?.(komaBeingEdited, value, nextSlots)
    }
    setEditingKoma(null)
  }

  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (isEnterKey(e) || e.key === 'Tab') {
      e.preventDefault()
      commitEdit()
      if (editingKoma !== null) {
        const next = editingKoma < 48 ? editingKoma + 1 : null
        if (next !== null) {
          setTimeout(() => {
            startEdit(next)
          }, 0)
        }
      }
    }
    if (e.key === 'Escape') {
      setEditingKoma(null)
    }
  }

  // startEdit は slots 等に依存するため毎レンダー再生成される。document
  // mouseup listener は mount 時に 1 度だけ登録するため、ref 経由で常に最新の
  // startEdit を読む (ForecastChart の latestRef ドラッグパターンと同じ、
  // stale closure 回避)。
  const startEditRef = useRef(startEdit)
  startEditRef.current = startEdit

  // 非同期 (クリップボード読取後) の編集解除で、その間に開始された別セルの編集
  // セッションを巻き込まないよう、最新の editingKoma を ref で参照する。
  const editingKomaRef = useRef(editingKoma)
  editingKomaRef.current = editingKoma

  // ── AC-11 ExcelGrid: ドラッグ範囲選択 (mousedown → mouseenter(drag) → mouseup) ──
  useEffect(() => {
    const handleMouseUp = (): void => {
      const start = dragStartKomaRef.current
      if (start === null) return
      dragStartKomaRef.current = null
      if (!didDragRef.current) {
        // ドラッグせず同一セルで mouseup = 通常クリック (既存の click-to-edit 挙動を維持)
        if (start.col === 1) startEditRef.current(start.row + 1)
      }
      didDragRef.current = false
    }
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [])

  const handleCellMouseDown = (pos: CellPos, shiftKey = false): void => {
    const koma = pos.row + 1
    // 編集中のセル自身への mousedown (Input 内でのテキスト選択操作等、
    // userEvent.clear/type が内部的に発行しうる) は cell 選択/ドラッグ扱いに
    // しない (= 誤って commitEdit で編集を中断させない)。
    if (editingKoma === koma) return
    if (editingKoma !== null) commitEdit()
    if (shiftKey && selectionRange !== null) {
      const anchor = selectionAnchorRef.current ?? pos
      setSelectionFocus(pos)
      setSelectionRange(makeRange(anchor, pos))
      return
    }
    selectionAnchorRef.current = pos
    setSelectionFocus(pos)
    dragStartKomaRef.current = pos
    didDragRef.current = false
    setSelectionRange(makeRange(pos, pos))
  }

  const handleCellMouseEnter = (pos: CellPos, buttons: number): void => {
    const start = dragStartKomaRef.current
    if (start === null || buttons !== 1) return
    if (start.row !== pos.row || start.col !== pos.col)
      didDragRef.current = true
    setSelectionFocus(pos)
    setSelectionRange(makeRange(start, pos))
  }

  const handleCellFocus = (pos: CellPos): void => {
    const guarded = focusSyncGuardRef.current
    if (guarded?.row === pos.row && guarded.col === pos.col) {
      focusSyncGuardRef.current = null
      return
    }
    if (selectionFocus.row === pos.row && selectionFocus.col === pos.col) return
    selectionAnchorRef.current = pos
    setSelectionFocus(pos)
    setSelectionRange(makeRange(pos, pos))
  }

  // ── AC-11 ExcelGrid: 矢印キー移動 / F2 編集開始 / Ctrl+C+V コピー貼付 ─────
  const handleCellKeyDown = (
    e: React.KeyboardEvent<HTMLElement>,
    pos: CellPos,
  ): void => {
    if (
      onUndoAggregate !== undefined &&
      (e.ctrlKey || e.metaKey) &&
      e.key.toLowerCase() === 'z' &&
      editingKoma === null
    ) {
      e.preventDefault()
      onUndoAggregate()
      return
    }
    const koma = pos.row + 1
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      (e.shiftKey && (e.key === 'ArrowLeft' || e.key === 'ArrowRight'))
    ) {
      e.preventDefault()
      const dr = e.key === 'ArrowUp' ? -1 : e.key === 'ArrowDown' ? 1 : 0
      const dc = e.key === 'ArrowLeft' ? -1 : e.key === 'ArrowRight' ? 1 : 0
      const nextPos = stepCell(pos, dr, dc, gridBounds) ?? pos
      const anchor = e.shiftKey ? (selectionAnchorRef.current ?? pos) : nextPos
      selectionAnchorRef.current = anchor
      setSelectionFocus(nextPos)
      setSelectionRange(makeRange(anchor, nextPos))
      focusSyncGuardRef.current = nextPos
      cellRefs.current[cellKey(nextPos)]?.focus()
      return
    }
    if (e.key === 'F2') {
      e.preventDefault()
      if (pos.col === 1) startEdit(koma, 'end')
      return
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') {
      e.preventDefault()
      const range = selectionRange ?? makeRange(pos, pos)
      const text = rangeToTsv(range, (row, col) => {
        const slot = slots[row]
        if (col === 0) return `${komaNumberToTime(row + 1)}〜`
        if (col === 1)
          return slot === undefined ? '' : slotValueToEditText(slot)
        if (col === 2)
          return computeSaveDiff(baselineSlots?.[row], slot).display.replace(
            /[\s,]/g,
            '',
          )
        if (showTodaySection && col === 3) {
          const actual = todayActualSlots?.[row]
          return actual === undefined
            ? ''
            : formatKwh(actual).replace(/[\s,]/g, '')
        }
        if (showTodaySection && col === 4) {
          const diff = computeSlotDiff(slot, todayActualSlots?.[row])
          const roundedDelta =
            diff === undefined ? undefined : roundSignedDelta(diff.delta)
          return roundedDelta === undefined
            ? ''
            : roundedDelta > 0
              ? `+${roundedDelta}`
              : String(roundedDelta)
        }
        const extra = extraColumns[col - 3 - (showTodaySection ? 2 : 0)]
        const extraValue = extra?.slots[row]
        return extraValue === undefined
          ? ''
          : extra?.valueKind === 'actual'
            ? formatKwh(extraValue).replace(/[\s,]/g, '')
            : // 確定履歴は需要想定値なので、本列と同じく保持値 (小数) のままコピーする
              slotValueToEditText(extraValue)
      })
      void navigator.clipboard?.writeText(text).catch(() => {
        // クリップボード API 未対応環境は no-op (AC-11 のコア価値は範囲選択・貼付側)
      })
      return
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'v') {
      e.preventDefault()
      // 編集中は編集対象コマを起点にする (startEdit が selectionRange を同期
      // するため通常は一致するが、起点の SoT を編集セル側に寄せる)。
      const start =
        editingKoma !== null
          ? { row: editingKoma - 1, col: 1 }
          : selectionRange !== null
            ? { row: selectionRange.rowMin, col: selectionRange.colMin }
            : pos
      const editingKomaAtPaste = editingKoma
      const editSessionAtPaste = editSessionRef.current
      const editRevisionAtPaste = editRevisionRef.current
      const pasteRequestId = ++pasteRequestIdRef.current
      navigator.clipboard
        ?.readText()
        .then((text) => {
          // 後から開始した貼付が先に完了した場合、古い結果で上書きしない。
          if (pasteRequestId !== pasteRequestIdRef.current) return
          const plan = planPaste(
            parseTsv(text),
            start,
            selectionRange,
            gridBounds,
          )
          const { targetRange } = plan
          // AC-DFC-EDIT-003: 貼付は数値として解釈できるセルを小数も含めそのまま反映する。
          const baseSlots = slotsRef.current
          const nextSlots = [...baseSlots]
          let skippedCount = 0
          let readOnlySkippedCount = 0
          let appliedCount = 0
          for (let row = targetRange.rowMin; row <= targetRange.rowMax; row++) {
            for (
              let col = targetRange.colMin;
              col <= targetRange.colMax;
              col++
            ) {
              if (col !== 1) {
                readOnlySkippedCount += 1
                continue
              }
              if (!isKomaEditable(row + 1)) continue
              const raw = plan.sourceAt(row, col) ?? ''
              const parsed =
                inputParser === undefined
                  ? parseCellNumber(raw)
                  : inputParser(raw.trim().replace(/,/g, ''))
              // Infinity や backend の長さ上限を超える極小値は保存できないため、
              // 非数値と同じく反映しない
              if (parsed === undefined || !isSavableSlotValue(parsed)) {
                skippedCount += 1
                continue
              }
              appliedCount += 1
              nextSlots[row] = parsed
            }
          }
          setPasteSkippedCount(skippedCount)
          setPasteReadOnlySkippedCount(readOnlySkippedCount)
          if (appliedCount > 0) {
            selectionAnchorRef.current = start
            setSelectionFocus(start)
            setSelectionRange(targetRange)
          }
          if (nextSlots.some((v, i) => v !== baseSlots[i])) {
            // 編集モード中 (単一クリックで input にフォーカスがある状態) の貼付では、
            // 編集中の下書きを commit せず破棄する。破棄しないと貼付後の blur /
            // Enter で commitEdit が編集前の値を書き戻し、貼付範囲の先頭コマだけ元の
            // 値に戻る (spec AC-DFD-004a「貼り付けた値を対象コマに一括反映」)。
            // 実際に反映がある場合のみ破棄する (空文字・非数値・小数のみ等で貼付が
            // no-op になったときに入力中の下書きを失わないため)。また非同期の間に
            // 別セルの編集が始まっていたら、その編集セッションは触らない。
            // 貼付開始時と同じ編集セッション (startEdit ごとに採番される ID) で、
            // かつ貼付開始後に入力操作が無い場合のみ解除する。読取待ちの間に
            // Escape 等で編集を終了して同じコマを同じ値で再編集した場合や、
            // 別の値を入力してから同じ文字列へ戻した場合は、ユーザーの新しい
            // 入力を破棄しないため解除しない。
            if (
              editingKomaAtPaste !== null &&
              editSessionRef.current === editSessionAtPaste &&
              editingKomaRef.current === editingKomaAtPaste &&
              editRevisionRef.current === editRevisionAtPaste
            ) {
              setEditingKoma(null)
              // input が外れるとフォーカスが body へ抜けるため、貼付開始セルへ戻して
              // 後続のキーボード操作 (矢印/F2) を継続できるようにする (conventions.md
              // 「Excelライクなグリッド編集」のキーボード完遂)。
              setTimeout(() => {
                cellRefs.current[cellKey(start)]?.focus()
              }, 0)
            }
            // spec AC-DFD-006「貼付は補正モードを問わず貼付範囲のみ上書き」:
            // onChange (単一セル編集、比例配分の対象) とは別経路の onPaste で
            // 呼び出し元へ通知する (demand-fix-edit-operation)。
            onPaste?.(start.row + 1, nextSlots)
          }
        })
        .catch(() => {
          // クリップボード API 未対応 / 拒否時は no-op
        })
    }
  }

  return (
    <>
      {pasteSkippedCount > 0 && (
        <Box marginBottom={2}>
          <CompactAlert
            role="alert"
            status="warning"
            title={t('page:demandForecastDaily.koma.pasteSkipped', {
              count: pasteSkippedCount,
            })}
          />
        </Box>
      )}
      {pasteReadOnlySkippedCount > 0 && (
        <Box marginBottom={2}>
          <CompactAlert
            role="alert"
            status="warning"
            title={t('page:demandForecastDaily.koma.pasteReadOnlySkipped', {
              count: pasteReadOnlySkippedCount,
            })}
          />
        </Box>
      )}
      {/* overflow は chakra prop ではなく style 側へ一本化する (貼り付き時だけ
          visible に切り替えるため。prop と style の二重指定にすると
          どちらが勝つか読めない)。 */}
      <Box
        ref={wrapRef}
        data-testid="koma-table-wrap"
        // e2e が表の器を引く入口 (role も名前も持たないため testid を使う)。
        style={komaTableWrapOverflowStyle(isSticky)}
      >
        <TableRoot
          ref={tableRef}
          width="100%"
          borderCollapse="collapse"
          size="sm"
        >
          <TableHeader>
            <TableRow>
              <TableColumnHeader
                style={{
                  ...headerCellStyle,
                  ...theadStickyStyle,
                  width: `${KOMA_COLUMN_WIDTH.time}px`,
                  textAlign: 'center',
                }}
              >
                {t('page:demandForecastDaily.koma.time')}
              </TableColumnHeader>
              <TableColumnHeader
                style={{
                  ...headerCellStyle,
                  ...theadStickyStyle,
                  width: `${KOMA_COLUMN_WIDTH.draftValue}px`,
                  textAlign: 'right',
                }}
              >
                {t('page:demandForecastDaily.koma.draftValue')}
                {sourceLabel !== undefined && sourceLabel !== '' && (
                  <SourceSubLabel>{sourceLabel}</SourceSubLabel>
                )}
              </TableColumnHeader>
              {/* AC-01/AC-DFD-001: 「保存済みとの差」列は 3 断面共通で常時表示。
                  見出しは design (forecast-today.html:4379) と同じ 2 段構成で、
                  主ラベルに AC-DFD-001 の列名「差」、 サブラベルに括弧内の説明
                  「保存済みとの差」 を置く。 説明だけを主ラベルにしていると列名が
                  AC から外れる (2026-08-31 デザイナー指摘の突合で判明)。 */}
              <TableColumnHeader
                style={{
                  ...headerCellStyle,
                  ...theadStickyStyle,
                  width: `${KOMA_COLUMN_WIDTH.saveDiff}px`,
                  textAlign: 'right',
                  whiteSpace: 'normal',
                }}
              >
                {t('page:demandForecastDaily.koma.saveDiffTitle')}
                <SourceSubLabel>
                  {t('page:demandForecastDaily.koma.saveDiff')}
                </SourceSubLabel>
              </TableColumnHeader>
              {showTodaySection && (
                <TableColumnHeader
                  width={`${KOMA_COLUMN_WIDTH.actual}px`}
                  textAlign="right"
                  // AC-DFD-006a-6: 当日実績の見出しのホバーでチャートの実績値を強調する
                  onMouseEnter={() => {
                    onHoverSeriesKey?.('todayActual')
                  }}
                  onMouseLeave={() => {
                    onHoverSeriesKey?.(undefined)
                  }}
                  style={{
                    ...headerCellStyle,
                    ...theadStickyStyle,
                    whiteSpace: 'normal',
                  }}
                >
                  {t('page:demandForecastDaily.koma.actual')}
                </TableColumnHeader>
              )}
              {/* AC-10/AC-29: 実績との差分は当日断面のみ (列ヘッダ自体を出さない) */}
              {showTodaySection && (
                <TableColumnHeader
                  style={{
                    ...headerCellStyle,
                    ...theadStickyStyle,
                    width: `${KOMA_COLUMN_WIDTH.actualDiff}px`,
                    textAlign: 'right',
                    whiteSpace: 'normal',
                  }}
                >
                  {/* 差列と同じ 2 段構成。 サブラベルは計算式
                      (design forecast-today.html:4384、 spec daily.md:53 の括弧内と一致)。 */}
                  {t('page:demandForecastDaily.koma.actualDiff')}
                  <SourceSubLabel>
                    {t('page:demandForecastDaily.koma.actualDiffSubLabel')}
                  </SourceSubLabel>
                </TableColumnHeader>
              )}
              {/* 追加列ヘッダー (過去実績 / 確定履歴) */}
              {extraColumns.map(renderExtraColumnHeader)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {Array.from({ length: 48 }, (_, i) => {
              const koma = i + 1
              const value = slots[i]
              const isEditing = editingKoma === koma
              const isManual = manualEditKomas.has(koma)
              // AC-02: 未保存 (dirty) コマの視覚区別。manualEditKomas (draft
              // 永続化される手修正コマ) とは別概念だが、同じ「強調 accent 背景」を
              // 共有する (どちらも「保存済みの値と異なる」ことを示す点は共通)。
              const isDirtyKoma = dirtyKomas.has(koma)
              const isGcLocked = isKomaGcLocked(koma)
              const isCurrentKoma = currentKoma > 0 && koma === currentKoma
              const actualValue = showTodaySection
                ? todayActualSlots?.[i]
                : undefined
              const isSelected = isInRange(selectionRange, i, 1)

              /**
               * 現在コマ (AC-DFD-002 [P0]「現在コマをハイライト表示する」) は
               * **地色ではなく輪郭**で示す。design `.koma-row-current td`
               * (`forecast-today.html:269-274`) の
               * `box-shadow: inset 0 0 0 1px var(--border-accent-primary)` 相当。
               *
               * 旧実装は行を `Surface.accentPrimaryLightLow` で塗っていたが、
               * **この token は同じ表で「このセルは編集できる」affordance に使っている**
               * (下の `draftCellBg` の既定枝 = design `.koma-cell-editable`)。
               * 現在コマは AC-DFD-002 前半節により**つねに GC 済み = 編集できない行**なので、
               * 同じ色を敷くと意味が逆に読める。AC-DFD-002 は「ハイライト表示する」としか
               * 書いておらず見せ方を決めていないため、design 側の輪郭表現に寄せた
               * (2026-09-02 デザイナー判断、issue #6872 の 5 番)。
               */
              const currentKomaOutline = isCurrentKoma
                ? `inset 0 0 0 1px ${LightModeSemanticColors.Border.accentPrimary.value}`
                : undefined

              /**
               * GC 境界コマ (= ロック帯の最終コマ) か。design の `.gc-row`
               * (`forecast-today.html` の `isGC = (d.koma === GC_KOMA)`) 相当。
               *
               * **ロック帯全体 (`isGcLocked`) とは別概念。** design は
               * 「warning 地 + bold」を**この 1 行だけ**に当て、ロック帯の残りは
               * セル側のハッチで表す。旧実装はこれを混同してロック帯を丸ごと
               * warning で塗っており、(a) 帯が広いほど warning の意味が薄まる
               * (b) 「編集できない」の主表現がセルでなく行になる、の 2 点で
               * design と食い違っていた (2026-09-02 デザイナー指摘
               * 「編集できないコマの表現が弱い」)。
               */
              const isGcBoundary = gcKoma > 0 && koma === gcKoma

              // 行背景色 (優先順位: GC 境界行 > zebra)。design L267 の
              // `.gc-row td { background: --surface-warning-low; font-weight: 700 }` を
              // **GC 境界の 1 行だけ**に当てる。それ以外は
              // design/css/components.css L810 の DS 既定 zebra
              // (`.ecl-table tbody tr:nth-child(even) { background: --surface-secondary }`)
              // に合わせて偶数コマ (koma=2,4,...,48) を surface-secondary にする。
              // 現在コマはここに枝を持たない (上記のとおり輪郭で示すため)。design 側も
              // `.koma-row-current` に background 規則を持たず、地色は gc-row / zebra に委ねる。
              let rowBg: string | undefined
              if (isGcBoundary) {
                rowBg = LightModeSemanticColors.Surface.warningLow.value
              } else if (koma % 2 === 0) {
                rowBg = LightModeSemanticColors.Surface.secondary.value
              }
              const lockedFontWeight = isGcBoundary ? 'bold' : undefined
              // 行 hover (design css/components.css:836
              // `.ecl-table tbody tr:hover { background: var(--hover-low-emphasis) }`、
              // forecast-today.html:259-260「hover 表現は DS .ecl-table の :hover (gray)
              // で統一」)。design も tr の background を丸ごと置き換えるため、zebra 行の
              // secondary が hover 中に消えるのは design と同じ挙動。
              // **GC 境界行だけ**除外する: この行の背景が業務上の意味色 (warning) なので
              // 単純に上書きすると hover 中だけ意味が消える (design も `.gc-row:hover td` に
              // 専用の混色を当てて意味色を残している、同 268)。
              // ロック帯の残りは除外しない。地色を持たなくなったので hover で消える意味色が
              // 無く、「編集できない」はセル側のハッチが持つ (design も `.koma-row-locked` の
              // hover 抑止は `.koma-cell-forecast` にだけ掛けて行には掛けていない)。
              // **現在コマも除外しない。** 同じ理由 (輪郭は box-shadow なので hover 背景に
              // 塗り潰されない)。
              // chakra の `interactive` variant は使わない — hover 色が colorPalette.subtle で
              // design の rgba(0,0,0,0.04) と一致しない。
              const rowHoverBg = isGcBoundary
                ? undefined
                : LightModeSemanticColors.Hover.lowEmphasis.value

              // 需要想定値列の編集可能セルスタイル (design `.koma-cell-editable` /
              // `.koma-cell-edited` 準拠)。確定済みでない・GCロックでない・現在コマ行で
              // ない (= 優先順位は既存の行背景と同じ) セルは常時 accent 背景を出し、
              // 手動編集済みセルはさらに強い accent 背景にする。
              const isEditableDraftCell =
                !isReadOnly && !isGcLocked && !isCurrentKoma
              // AC-039 前段: dirty コマの視覚区別は read-only (BG全体〈複数小売〉
              // ビュー等) でも出す。isEditableDraftCell (accent 背景 + hover) は
              // 編集可否そのものの表現なので、dirty ハイライトはそれとは独立に
              // 判定する (manual 編集ハイライトは引き続き編集可能時のみ)。
              const isReadOnlyDirtyCell =
                isReadOnly && isDirtyKoma && !isGcLocked && !isCurrentKoma
              /**
               * 編集できない需要想定値セルの表現 (design `.koma-row-locked
               * .koma-cell-forecast`、`forecast-today.html:324-333`)。
               *
               * ```css
               * cursor: not-allowed; color: var(--text-mid-emphasis); box-shadow: none;
               * background:
               *   repeating-linear-gradient(135deg, transparent 0 5px,
               *     color-mix(in srgb, var(--text-low-emphasis) 14%, transparent) 5px 6px),
               *   var(--surface-secondary);
               * ```
               *
               * design のコメントに経緯がある: 「旧: 行全体 opacity:0.55 で薄くするだけで
               * disabled と伝わりにくく、値も読みづらかった → セル側で明示に変更」
               * (2026-07-16 ユーザー指示)。実装はこの**セル側の明示**を移植しておらず、
               * 「編集できない」が行の warning 地だけで表現されていて弱かった
               * (2026-09-02 デザイナー指摘)。
               *
               * 対象は `!isKomaEditable` = GC ロック + 参照専用ビュー (確定済み /
               * BG 全体〈複数小売〉)。design の `_dftIsLocked` と同じ集合。
               */
              const isLockedCell = !isKomaEditable(koma)
              const lockedCellBg = `repeating-linear-gradient(135deg, transparent 0 5px, color-mix(in srgb, ${LightModeSemanticColors.Text.lowEmphasis.value} 14%, transparent) 5px 6px), ${LightModeSemanticColors.Surface.secondary.value}`

              // 強い accent 背景を出す 2 つの状態。手動編集済みは編集できるセルだけ、
              // 未保存 (dirty) は参照専用ビューでも出す (上記 isReadOnlyDirtyCell)。
              // 状態の観測 seam として下の data 属性にも同じ値を出す。
              const showsManualEdited = isEditableDraftCell && isManual
              const showsDirty =
                isDirtyKoma && (isEditableDraftCell || isReadOnlyDirtyCell)

              const draftCellBg =
                showsManualEdited || showsDirty
                  ? LightModeSemanticColors.Surface.accentPrimaryLightMid.value
                  : isEditableDraftCell
                    ? LightModeSemanticColors.Surface.accentPrimaryLightLow
                        .value
                    : isLockedCell
                      ? lockedCellBg
                      : undefined
              const draftCellHoverBg = isEditableDraftCell
                ? LightModeSemanticColors.Surface.accentPrimaryLightMid.value
                : undefined
              // 需要想定値セルの枠。design の 2 つを 1 本の boxShadow で描き分ける:
              // - 編集可 affordance: `.koma-cell-editable`
              //   `box-shadow: inset 0 0 0 1px var(--surface-accent-primary-light-mid)`
              //   (forecast-today.html:336)
              // - 選択: `.koma-cell-selected`
              //   `outline: 2px solid var(--surface-accent-primary); outline-offset: -2px`
              //   (同 339) と視覚的に等価な inset 2px。--surface-accent-primary と
              //   Border.accentPrimary は同値 (#0c00c5)。
              // 選択枠は affordance 枠を置き換える (兄弟実装
              // Supply48Data/components/GridCell.tsx:122-130 と同じ視覚言語)。design の
              // `background: var(--surface-tertiary) !important` と範囲選択の
              // `color-mix(accent 18%)` は移植しない — 同じ td に spec 由来の背景
              // (AC-DFD-039 の未保存 accent、GC ロックの warning、現在コマ) が既に載って
              // おり、選択中だけ潰すと状態表示が消えるため。単一選択と範囲選択で枠の
              // 太さは分けない (GridCell.tsx:86-90 と同じ判断)。
              // 3 つ目の枝が現在コマの輪郭。`isEditableDraftCell` は
              // `!isCurrentKoma` を含むので、affordance 枠と競合しない
              // (= 現在コマ行がここに来る時は必ず「編集不可」側)。
              const cellBoxShadow = isSelected
                ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
                : isEditableDraftCell
                  ? `inset 0 0 0 1px ${LightModeSemanticColors.Surface.accentPrimaryLightMid.value}`
                  : currentKomaOutline

              // 需要想定値セル本体 (2026-09-02 デザイナー指摘: GC ロック済みコマに
              // 「なぜ編集できないか」の理由 tooltip を追加)。変数に切り出すのは、
              // 下の return 側で isGcLocked のときだけ HintTooltip (DS Tooltip) で
              // 包み、ロックでない残り (通常 47 行以下) には Tooltip trigger を
              // 一切マウントしない (空 tooltip を全セルに付けない) ための分岐を
              // JSX の複製なしで書くため。
              const draftValueCell = (
                <TableCell
                  ref={(el: HTMLTableCellElement | null) => {
                    cellRefs.current[cellKey({ row: i, col: 1 })] = el
                  }}
                  background={draftCellBg}
                  _hover={
                    draftCellHoverBg !== undefined
                      ? { background: draftCellHoverBg }
                      : undefined
                  }
                  data-dirty={showsDirty || undefined}
                  data-edit-affordance={isEditableDraftCell || undefined}
                  data-grid-col={1}
                  data-grid-row={i}
                  data-locked={isLockedCell || undefined}
                  data-manual-edited={showsManualEdited || undefined}
                  data-selected={isSelected || undefined}
                  style={{
                    ...komaBodyCellStyle(i),
                    textAlign: 'right',
                    // design `.koma-row-locked .koma-cell-forecast { cursor: not-allowed }`。
                    // `default` だと「ただの表示セル」と区別が付かず、ハッチと合わせて
                    // 「触れない」を伝えるのがこの cursor の役目 (2026-07-16 design 指示)。
                    cursor: isLockedCell ? 'not-allowed' : 'cell',
                    // 選択枠・編集可 affordance 枠 (cellBoxShadow の宣言箇所の
                    // コメント参照)。選択状態の観測 seam は、暗黙 role `cell` で
                    // aria-allowed-attr 違反にならない `data-selected` を使う。
                    boxShadow: cellBoxShadow,
                    // design `.koma-cell-editing { padding: 0 !important }`
                    // (forecast-today.html:347): 編集枠がセルを埋め、行高が編集の
                    // 前後で変わらないようにする。
                    ...(isEditing ? { padding: 0 } : {}),
                  }}
                  tabIndex={
                    isEditing
                      ? undefined
                      : selectionFocus.row === i && selectionFocus.col === 1
                        ? 0
                        : -1
                  }
                  // native title は DS Tooltip (HintTooltip、呼び出し元) 非対応環境
                  // (タッチ・支援技術の一部) 向けの保険 (ButtonTooltip/HintTooltip の
                  // JSDoc 準拠、2026-08-17 デザイナー既定)。文言は Tooltip 側と揃える。
                  title={isGcLocked ? gcLockedTooltipText : undefined}
                >
                  {isEditing ? (
                    // [DS差異] design `.koma-grid-input` (forecast-today.html:348-354)
                    // に合わせ、DS Input recipe の「高さ 36px 固定 / 1px 灰枠 / 角丸」を
                    // 打ち消してセルを埋める 2px accent 枠にする。Excel 同等の「セルを
                    // 埋める編集枠」で、編集の前後で行高を変えないため。fontSize は
                    // 触らない (constants/system.ts の input recipe override が既に
                    // 14px = design と一致)。生 input への置き換えはしない (本ファイル
                    // 冒頭の方針「UI は DS の Table / Input primitives で組む」)。
                    <Input
                      ref={inputRef}
                      // `height: 100%` は design `.koma-grid-input` と同じ
                      // (forecast-today.html:349)。 table cell 配下の百分率高さは
                      // 行の高さに対して解決されるので、 **入力欄は行高を決めない**
                      // — 行高は同じ行の他セル (時刻・差 等、通常の padding を持つ)
                      // が決める。 これが「編集の前後で行高が変わらない」根拠。
                      //
                      // ただし DS Input recipe の `min-height` が残ると
                      // `height: 100%` より優先されて行を押し広げるため 0 に潰す
                      // (レビュー指摘 #6863: 編集時に行高が跳ねる)。
                      height="100%"
                      minHeight={0}
                      textAlign="right"
                      borderWidth="2px"
                      borderColor="Border.accentPrimary"
                      borderRadius="none"
                      _focusVisible={{
                        borderColor: 'Border.accentPrimary',
                        outlineColor: 'Border.accentPrimary',
                      }}
                      fontVariantNumeric="tabular-nums"
                      inputMode="numeric"
                      // chakra input recipe の variant outline は
                      // `focusVisibleRing: "inside"` (chakra 3.29.0
                      // theme/recipes/input.js:64-69) で focus 時に borderColor を
                      // focus-ring 色へ差し替えるため、明示的に打ち消さないと編集に
                      // 入った瞬間だけ accent 枠が消える。
                      onBlur={commitEdit}
                      onChange={(e) => {
                        editRevisionRef.current += 1
                        // design note demand-disable-ime Decision 1/2: IME 由来の
                        // 全角/かな入力を入力段階で除去する (小数点・負号は通す。
                        // AC-DFC-EDIT-003: 小数は拒否しない)。
                        const filtered = filterNumericImeInput(e.target.value)
                        // 非許容文字だけの入力 (IME 全角が選択範囲を置換した等) で
                        // 編集値が全消しになる場合は、既存値を削除せず直前の編集値を
                        // 保持する (React が controlled value へ DOM を復元する)。raw が
                        // 空の場合 (意図的な全削除) は空欄化として許可する。既存値が
                        // 黙って消える経路を作らないため (codex review #6407、
                        // 「値が消えたのに理由が分からない状態を作らない」)。
                        if (e.target.value !== '' && filtered === '') return
                        setEditValue(filtered)
                      }}
                      onKeyDown={handleInputKeyDown}
                      paddingX="2"
                      size="sm"
                      type="text"
                      value={editValue}
                    />
                  ) : value !== undefined ? (
                    // eslint-disable-next-line ebalance/no-adhoc-typography -- fontWeight は呼び出し元 (lockedFontWeight) 経由の動的値のため静的 textStyle 化不能。chakra Table host の ambient lineHeight 制約もあり (design 併記: td=md/400、td:first-child=md/600、.gc-row td=md/700)
                    <Text
                      as="span"
                      // design `.koma-row-locked .koma-cell-forecast { color:
                      // var(--text-mid-emphasis) }`。ハッチ・not-allowed と合わせて
                      // 「このセルは触れない」を 3 点で伝える。値は読めるままにする
                      // (design のコメント: 旧 opacity 0.55 は値まで読みづらかった)。
                      color={
                        isLockedCell ? 'Text.midEmphasis' : 'Text.highEmphasis'
                      }
                      fontSize="md"
                      fontWeight={lockedFontWeight}
                      fontVariantNumeric="tabular-nums"
                    >
                      {formatSlotValue(value)}
                    </Text>
                  ) : (
                    <EmptyCell />
                  )}
                </TableCell>
              )

              /**
               * GC 境界行の直後に差し込む「ここから編集できます」の区切り行
               * (design `.koma-edit-divider-row`)。**編集できる行が実際に続く時だけ**出す
               * — 参照専用ビュー (確定済み / BG 全体〈複数小売〉) は全コマがロックなので
               * この案内は嘘になり、GC が最終コマ (48) の日も同じ。
               *
               * design はここで「セルを選んで入力、または F2・ダブルクリック」と書くが、
               * **[design 差異]** 本実装は type-to-edit を持たない (`startEdit` の呼び出し元は
               * mouseup の click-to-edit・F2・ダブルクリックのみ)。無い操作を案内しないため
               * 文言を変える (ダブルクリックを案内に足すかは #6905)。
               *
               * 行を 1 つ増やすがグリッドの座標には影響しない。セルの参照・キーボード移動は
               * すべて論理 index (`cellRefs` の `{row, col}` / `data-grid-row`) で解決しており、
               * DOM 上の行順を数える経路が無いため (本ファイルに `rows[i]` /
               * `nextElementSibling` の類は存在しない)。区切り行には `data-grid-*` を付けない。
               */
              const showsEditDivider =
                isGcBoundary && koma < KOMA_COUNT && isKomaEditable(koma + 1)

              const komaRow = (
                <TableRow
                  key={koma}
                  _hover={
                    rowHoverBg !== undefined
                      ? { background: rowHoverBg }
                      : undefined
                  }
                  aria-label={
                    isCurrentKoma
                      ? `${komaNumberToTime(koma)} (現在コマ)`
                      : isGcLocked
                        ? `${komaNumberToTime(koma)} (${t('page:demandForecastDaily.koma.gcLocked')})`
                        : undefined
                  }
                  backgroundColor={rowBg}
                  data-gc-boundary={isGcBoundary || undefined}
                  onDoubleClick={(e: React.MouseEvent<HTMLElement>) => {
                    const cell = (e.target as HTMLElement).closest<HTMLElement>(
                      '[data-grid-row][data-grid-col]',
                    )
                    if (cell === null) return
                    handleCellDoubleClick({
                      row: Number(cell.dataset.gridRow),
                      col: Number(cell.dataset.gridCol),
                    })
                  }}
                  onFocus={(e: React.FocusEvent<HTMLElement>) => {
                    const cell = (e.target as HTMLElement).closest<HTMLElement>(
                      '[data-grid-row][data-grid-col]',
                    )
                    if (cell === null) return
                    handleCellFocus({
                      row: Number(cell.dataset.gridRow),
                      col: Number(cell.dataset.gridCol),
                    })
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLElement>) => {
                    const cell = (e.target as HTMLElement).closest<HTMLElement>(
                      '[data-grid-row][data-grid-col]',
                    )
                    if (cell === null) return
                    handleCellKeyDown(e, {
                      row: Number(cell.dataset.gridRow),
                      col: Number(cell.dataset.gridCol),
                    })
                  }}
                  onMouseDown={(e: React.MouseEvent<HTMLElement>) => {
                    const cell = (e.target as HTMLElement).closest<HTMLElement>(
                      '[data-grid-row][data-grid-col]',
                    )
                    if (cell === null) return
                    handleCellMouseDown(
                      {
                        row: Number(cell.dataset.gridRow),
                        col: Number(cell.dataset.gridCol),
                      },
                      e.shiftKey,
                    )
                  }}
                  onMouseOver={(e: React.MouseEvent<HTMLElement>) => {
                    const cell = (e.target as HTMLElement).closest<HTMLElement>(
                      '[data-grid-row][data-grid-col]',
                    )
                    if (cell === null) return
                    handleCellMouseEnter(
                      {
                        row: Number(cell.dataset.gridRow),
                        col: Number(cell.dataset.gridCol),
                      },
                      e.buttons,
                    )
                  }}
                >
                  {/* AC-15: 時刻列 (列ホバー強調) + AC-09: HH:mm〜 形式 */}
                  <TableCell
                    ref={(el: HTMLTableCellElement | null) => {
                      cellRefs.current[cellKey({ row: i, col: 0 })] = el
                    }}
                    data-grid-col={0}
                    data-grid-row={i}
                    data-selected={isInRange(selectionRange, i, 0) || undefined}
                    onMouseEnter={() => {
                      onHoverKoma?.(koma)
                    }}
                    onMouseLeave={() => {
                      onHoverKoma?.(undefined)
                    }}
                    style={{
                      ...komaBodyCellStyle(i),
                      textAlign: 'center',
                      // 選択枠が最優先。無選択なら現在コマの輪郭を出す
                      // (design `.koma-row-current td` は行の全 td に inset 1px を掛ける)。
                      boxShadow: isInRange(selectionRange, i, 0)
                        ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
                        : currentKomaOutline,
                    }}
                    tabIndex={
                      selectionFocus.row === i && selectionFocus.col === 0
                        ? 0
                        : -1
                    }
                  >
                    {/* eslint-disable-next-line ebalance/no-adhoc-typography -- fontWeight は呼び出し元 (lockedFontWeight) 経由の動的値のため静的 textStyle 化不能。chakra Table host の ambient lineHeight 制約もあり (design 併記: td=md/400、td:first-child=md/600、.gc-row td=md/700) */}
                    <Text
                      as="span"
                      color="Text.highEmphasis"
                      fontSize="md"
                      fontWeight={lockedFontWeight ?? 'semibold'}
                    >
                      {komaNumberToTime(koma)}〜
                    </Text>
                  </TableCell>
                  {/* 需要想定値 (spec AC-001、ExcelGrid操作対応 AC-11)。GC ロック済み
                      コマ (isGcLocked) だけ「なぜ編集できないか」を DS Tooltip
                      (HintTooltip) で示す (2026-09-02 デザイナー指摘)。ロックでない
                      コマ (通常時は 47 行以上) には Tooltip を一切マウントしない
                      (isHidden での常時 wrap にすると空 tooltip trigger が全セルに
                      付くため、条件付き mount にして避ける)。 */}
                  {isGcLocked ? (
                    <HintTooltip content={gcLockedTooltipText}>
                      {draftValueCell}
                    </HintTooltip>
                  ) : (
                    draftValueCell
                  )}
                  {/* AC-01/AC-DFD-001: 「差」(保存済みとの差分)、3断面共通 */}
                  <TableCell
                    ref={(el: HTMLTableCellElement | null) => {
                      cellRefs.current[cellKey({ row: i, col: 2 })] = el
                    }}
                    data-grid-col={2}
                    data-grid-row={i}
                    data-selected={isInRange(selectionRange, i, 2) || undefined}
                    style={{
                      ...komaBodyCellStyle(i),
                      textAlign: 'right',
                      boxShadow: isInRange(selectionRange, i, 2)
                        ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
                        : currentKomaOutline,
                    }}
                    tabIndex={
                      selectionFocus.row === i && selectionFocus.col === 2
                        ? 0
                        : -1
                    }
                  >
                    <SaveDiffCell
                      baseline={baselineSlots?.[i]}
                      current={value}
                    />
                  </TableCell>
                  {showTodaySection && (
                    <TableCell
                      ref={(el: HTMLTableCellElement | null) => {
                        cellRefs.current[cellKey({ row: i, col: 3 })] = el
                      }}
                      data-grid-col={3}
                      data-grid-row={i}
                      data-selected={
                        isInRange(selectionRange, i, 3) || undefined
                      }
                      style={{
                        ...komaBodyCellStyle(i),
                        textAlign: 'right',
                        boxShadow: isInRange(selectionRange, i, 3)
                          ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
                          : currentKomaOutline,
                      }}
                      tabIndex={
                        selectionFocus.row === i && selectionFocus.col === 3
                          ? 0
                          : -1
                      }
                    >
                      {actualValue === undefined ? (
                        <EmptyCell />
                      ) : (
                        // eslint-disable-next-line ebalance/no-adhoc-typography -- fontWeight は呼び出し元 (lockedFontWeight) 経由の動的値のため静的 textStyle 化不能。chakra Table host の ambient lineHeight 制約もあり (design 併記: td=md/400、td:first-child=md/600、.gc-row td=md/700)
                        <Text
                          as="span"
                          color="Text.highEmphasis"
                          fontSize="md"
                          fontWeight={lockedFontWeight}
                          fontVariantNumeric="tabular-nums"
                        >
                          {/* 実績は託送由来の送電端値で小数を持つため四捨五入する
                            (formatKwh の godoc / 2026-07-31 竹ノ下さん指示)。想定値
                            (上の列) も表示は整数 (formatSlotValue → formatKwh、
                            保持値は小数のまま。AC-DFC-EDIT-003)。 */}
                          {formatKwh(actualValue)}
                        </Text>
                      )}
                    </TableCell>
                  )}
                  {/* 実績との差分 (spec AC-001、AC-10): 実績−想定、当日断面のみ、%表示なし */}
                  {showTodaySection && (
                    <TableCell
                      ref={(el: HTMLTableCellElement | null) => {
                        cellRefs.current[cellKey({ row: i, col: 4 })] = el
                      }}
                      data-grid-col={4}
                      data-grid-row={i}
                      data-selected={
                        isInRange(selectionRange, i, 4) || undefined
                      }
                      style={{
                        ...komaBodyCellStyle(i),
                        textAlign: 'right',
                        boxShadow: isInRange(selectionRange, i, 4)
                          ? `inset 0 0 0 2px ${LightModeSemanticColors.Border.accentPrimary.value}`
                          : currentKomaOutline,
                      }}
                      tabIndex={
                        selectionFocus.row === i && selectionFocus.col === 4
                          ? 0
                          : -1
                      }
                    >
                      <ActualDiffCell actual={actualValue} forecast={value} />
                    </TableCell>
                  )}
                  {/* 追加列セル (過去実績 / 確定履歴) */}
                  {extraColumns.map((col, extraIndex) =>
                    renderExtraColumnCell(
                      col,
                      i,
                      lockedFontWeight,
                      3 + (showTodaySection ? 2 : 0) + extraIndex,
                      isInRange(
                        selectionRange,
                        i,
                        3 + (showTodaySection ? 2 : 0) + extraIndex,
                      ),
                      currentKomaOutline,
                    ),
                  )}
                </TableRow>
              )

              if (!showsEditDivider) return komaRow
              return (
                <Fragment key={koma}>
                  {komaRow}
                  <TableRow>
                    {/* design `.koma-edit-divider-row td` (forecast-today.html:339):
                        `padding: 4px 12px; font-size: var(--font-size-xs);
                         color: var(--text-mid-emphasis);
                         background: var(--surface-accent-primary-light-low);
                         border-top: 2px dashed var(--surface-accent-primary);
                         white-space: nowrap`。

                        文字は caption (12px)。この行は「ここから編集できる」を伝える
                        affordance そのもので、10px (design の旧値) は本文 md=14px に対して
                        2 段下になり読み飛ばされやすい。「編集できる・できないの表現が弱い」
                        というデザイナー指摘 (2026-09-02) の趣旨と逆に働くため上げた。
                        **design 側も同日 12px へ揃えた**ので design 差異ではない
                        (design-desk の `.koma-edit-divider-row td`)。

                        border は 3 プロパティに分けて書く。ショートハンドで色を省くと
                        `currentColor` が効いて黒線になる。 */}
                    <TableCell
                      textStyle="caption"
                      color="Text.midEmphasis"
                      background="Surface.accentPrimaryLightLow"
                      borderTopWidth="2px"
                      borderTopStyle="dashed"
                      borderTopColor="Surface.accentPrimary"
                      whiteSpace="nowrap"
                      colSpan={
                        3 + (showTodaySection ? 2 : 0) + extraColumns.length
                      }
                      paddingX={3}
                      paddingY={1}
                    >
                      <FaArrowDownRegular
                        boxSize={iconSize.gridEditHint}
                        verticalAlign="middle"
                        marginEnd={1}
                        aria-hidden="true"
                      />
                      {t('page:demandForecastDaily.koma.editDivider', {
                        time: komaNumberToTime(koma + 1),
                      })}
                    </TableCell>
                  </TableRow>
                </Fragment>
              )
            })}
          </TableBody>
        </TableRoot>
      </Box>
    </>
  )
}
